<template>
  <v-container fluid>
    <div class="mb-5">
      <material-card color="primary" title="Pengajuan Tandatangan Dokumen" class="card-wrapper">
        <v-form ref="form" lazy-validation class="pj">
          <!-- Pilihan Tipe Upload -->
          <div v-if="!uploadTypeSelected" class="pj-type">
            <p class="pj-type__lead">Berapa dokumen yang ingin Anda ajukan?</p>
            <div class="pj-type__options">
              <button type="button" class="pj-type__option" @click="selectUploadType('per_dokumen')">
                <v-icon class="pj-type__icon">mdi-file-document-outline</v-icon>
                <span class="pj-type__title">Satu dokumen</span>
                <span class="pj-type__desc">Unggah satu file PDF atau Word, isi nomor surat, lalu atur posisi tanda tangan.</span>
              </button>
              <button type="button" class="pj-type__option" @click="selectUploadType('massal')">
                <v-icon class="pj-type__icon">mdi-folder-zip-outline</v-icon>
                <span class="pj-type__title">Banyak dokumen sekaligus</span>
                <span class="pj-type__desc">Unggah file ZIP berisi dokumen dan file CSV berisi daftar nomor suratnya.</span>
              </button>
            </div>
          </div>

          <template v-else>
            <div class="pj-typebar">
              <span>
                <v-icon small>{{ uploadTypeSelected === 'massal' ? 'mdi-folder-zip-outline' : 'mdi-file-document-outline' }}</v-icon>
                {{ uploadTypeSelected === 'massal' ? 'Banyak dokumen sekaligus' : 'Satu dokumen' }}
              </span>
              <v-btn text small color="primary" @click="resetUploadType">Ganti tipe</v-btn>
            </div>

            <!-- 1. Jenis dokumen -->
            <section class="pj-step">
              <h3 class="pj-step__title"><span class="pj-step__num">1</span>Jenis dokumen</h3>
              <v-row dense>
                <v-col cols="12" md="6">
                  <p class="label mb-1">Organisasi (PAU/Fakultas/Sekolah) <span class="pj-req">*</span></p>
                  <v-autocomplete
                      :items="filteredOrganisasi"
                      :item-text="item => item.nama"
                      :item-value="item => item.id"
                      :rules="organisasiRules"
                      no-data-text="Tidak ada data"
                      placeholder="Pilih organisasi"
                      clearable
                      dense
                      outlined
                      v-model="formData.id_organisasi"
                      @change="setDropdownJenisDokumenOrganisasi"
                      @keydown.enter.prevent
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <p class="label mb-1">Jenis dokumen <span class="pj-req">*</span></p>
                  <v-autocomplete
                      :items="filteredJenisDokumenOrganisasi"
                      :item-text="item => item.NamaDokumen"
                      :item-value="item => item.Id"
                      @change="setDetailJenisDokumenOrganisasi"
                      :rules="jenisDokumenOrganisasiRules"
                      :disabled="formData.id_organisasi == null"
                      :placeholder="formData.id_organisasi == null ? 'Pilih organisasi terlebih dahulu' : 'Pilih jenis dokumen'"
                      no-data-text="Tidak ada data"
                      clearable
                      dense
                      outlined
                      v-model="formData.id_jenis_dokumen_organisasi"
                      @keydown.enter.prevent
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <div v-if="formData.id_jenis_dokumen_organisasi != null" class="pj-info">
                <dl class="pj-info__facts">
                  <div>
                    <dt>Sifat kerahasiaan</dt>
                    <dd><esign-status-tag tone="neutral" :value="tingkatKerahasiaan || '-'" /></dd>
                  </div>
                  <div>
                    <dt>Alur persetujuan</dt>
                    <dd>{{ workflowPersetujuan || '-' }}</dd>
                  </div>
                  <div>
                    <dt>Template</dt>
                    <dd>
                      <span v-if="fileTemplate === ''" class="pj-muted">{{ keteranganFileTemplate || 'Tidak ada template' }}</span>
                      <v-btn v-else small outlined color="primary" @click="downloadFile()">
                        <v-icon left small>mdi-download</v-icon>{{ displayFileTemplate }}
                      </v-btn>
                      <v-btn v-if="uploadTypeSelected === 'massal'" small outlined color="primary" @click="downloadTemplateCsv()">
                        <v-icon left small>mdi-download</v-icon>Template CSV
                      </v-btn>
                    </dd>
                  </div>
                </dl>
              </div>
            </section>

            <!-- 2. Dokumen -->
            <section v-if="formData.id_jenis_dokumen_organisasi != null" class="pj-step">
              <h3 class="pj-step__title"><span class="pj-step__num">2</span>Dokumen</h3>
              <v-row v-if="uploadTypeSelected === 'per_dokumen'" dense>
                <v-col cols="12" md="6">
                  <p class="label mb-1">File dokumen <span class="pj-req">*</span></p>
                  <v-file-input
                      accept=".pdf, .doc, .docx"
                      v-model="file"
                      placeholder="Pilih file"
                      prepend-icon=""
                      prepend-inner-icon="mdi-paperclip"
                      dense
                      outlined
                      @change="loadSourcePdf()"
                      :rules="fileRules"
                  ></v-file-input>
                  <p class="pj-hint">PDF, DOC, atau DOCX</p>
                </v-col>
                <v-col cols="12" md="6">
                  <p class="label mb-1">Nomor surat/dokumen <span class="pj-req">*</span></p>
                  <v-text-field
                      placeholder="Contoh: SRT/1/00003/2026"
                      outlined
                      dense
                      clearable
                      v-model="formData.nomor_surat"
                      :rules="nomorSuratRules"
                  />
                </v-col>
              </v-row>
              <v-row v-else dense>
                <v-col cols="12" md="6">
                  <p class="label mb-1">File ZIP berisi dokumen <span class="pj-req">*</span></p>
                  <v-file-input
                      accept=".zip"
                      v-model="fileMassal.zip"
                      placeholder="Pilih file .zip"
                      prepend-icon=""
                      prepend-inner-icon="mdi-folder-zip-outline"
                      dense
                      outlined
                      :rules="fileMassalZipRules"
                  ></v-file-input>
                  <p class="pj-hint">Isi ZIP: file .pdf, .doc, atau .docx</p>
                </v-col>
                <v-col cols="12" md="6">
                  <p class="label mb-1">File CSV daftar nomor surat <span class="pj-req">*</span></p>
                  <v-file-input
                      accept=".csv"
                      v-model="fileMassal.csv"
                      placeholder="Pilih file .csv"
                      prepend-icon=""
                      prepend-inner-icon="mdi-file-delimited-outline"
                      dense
                      outlined
                      :rules="fileMassalCsvRules"
                  ></v-file-input>
                  <p class="pj-hint">Format tiap baris: Nama_File;Nomor_Surat</p>
                </v-col>
              </v-row>
            </section>

            <!-- 3. Pemaraf & penandatangan -->
            <section v-if="formData.id_jenis_dokumen_organisasi != null" class="pj-step">
              <h3 class="pj-step__title"><span class="pj-step__num">3</span>Pemaraf &amp; penandatangan</h3>
              <div class="pj-switch">
                <v-switch v-model="atur" inset dense hide-details color="primary" @change="aturUlang()" />
                <div>
                  <p class="pj-switch__label">Ubah susunan penandatangan</p>
                  <p class="pj-switch__hint">Susunan di bawah diambil dari pengaturan jenis dokumen. Aktifkan bila perlu diubah untuk dokumen ini.</p>
                </div>
              </div>

              <div v-if="atur" class="pj-jenis-ttd">
                <p class="label mb-1">Jenis tanda tangan tambahan</p>
                <v-select
                  :items="jenisTtds"
                  :item-text="item => item.Nama"
                  :item-value="item => item.Id"
                  no-data-text="Tidak ada data"
                  placeholder="Pilih jenis tanda tangan"
                  v-model="chosenIdJenisTtds"
                  @change="loadImages(true)"
                  multiple
                  chips
                  small-chips
                  dense
                  outlined
                  hide-details
                />
              </div>

              <!-- Daftar Pemaraf -->
              <div class="pj-people">
                <h4 class="pj-people__title">Pemaraf <span class="pj-muted">({{ chosenPemarafs.length }})</span></h4>
                <div v-if="atur" class="pj-people__add">
                  <v-autocomplete
                    :items="dropdownPemarafTtd"
                    :item-text="item => item.nama_lengkap + ' - ' + item.jabatan + ' - ' + item.nik + ' - ' + item.nama_organisasi"
                    :item-value="item => item"
                    placeholder="Cari nama, jabatan, atau NIK pemaraf"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    no-data-text="Tidak ada data"
                    v-model="selectedPemaraf"
                    :loading="loading"
                    :error-messages="pemarafError"
                    @keydown.enter.prevent
                  ></v-autocomplete>
                  <v-btn depressed color="primary" @click="addPemaraf">
                    <v-icon left small>mdi-plus</v-icon>Tambah
                  </v-btn>
                </div>
                <ol v-if="chosenPemarafs.length > 0" class="pj-people__list">
                  <li v-for="i in chosenPemarafs" :key="'paraf-' + i.urutan_hirarki" class="pj-person">
                    <span class="pj-person__num">{{ i.urutan_hirarki }}</span>
                    <div class="pj-person__who">
                      <p class="pj-person__name">{{ i.nama_lengkap }}</p>
                      <p class="pj-person__meta">{{ i.jabatan }} · {{ i.organisasi }} · NIK {{ i.nik }}</p>
                    </div>
                    <v-checkbox
                      v-model="i.flag_visual"
                      :disabled="!atur"
                      label="Tampilkan paraf"
                      dense
                      hide-details
                      class="pj-person__visual"
                      @change="loadImages(true)"
                    />
                    <v-btn v-if="atur" icon small color="error" aria-label="Hapus pemaraf" @click="hapusPemaraf(i.urutan_hirarki)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </li>
                </ol>
                <p v-else class="pj-empty">Belum ada pemaraf.</p>
              </div>

              <!-- Daftar Penandatangan -->
              <div class="pj-people">
                <h4 class="pj-people__title">Penandatangan <span class="pj-muted">({{ chosenPenandatangans.length }})</span></h4>
                <div v-if="atur" class="pj-people__add">
                  <v-autocomplete
                      :items="dropdownPemarafTtd"
                      :item-text="item => item.nama_lengkap + ' - ' + item.jabatan + ' - ' + item.nik + ' - ' + item.nama_organisasi"
                      :item-value="item => item"
                      placeholder="Cari nama, jabatan, atau NIK penandatangan"
                      clearable
                      dense
                      outlined
                      hide-details="auto"
                      no-data-text="Tidak ada data"
                      v-model="selectedPenandatangan"
                      :loading="loading"
                      :error-messages="penandatanganError"
                      @keydown.enter.prevent
                  ></v-autocomplete>
                  <v-btn depressed color="primary" @click="addPenandatangan">
                    <v-icon left small>mdi-plus</v-icon>Tambah
                  </v-btn>
                </div>
                <ol v-if="chosenPenandatangans.length > 0" class="pj-people__list">
                  <li v-for="i in chosenPenandatangans" :key="'ttd-' + i.urutan_hirarki" class="pj-person">
                    <span class="pj-person__num">{{ i.urutan_hirarki }}</span>
                    <div class="pj-person__who">
                      <p class="pj-person__name">{{ i.nama_lengkap }}</p>
                      <p class="pj-person__meta">{{ i.jabatan }} · {{ i.organisasi }} · NIK {{ i.nik }}</p>
                    </div>
                    <v-checkbox
                      v-model="i.flag_visual"
                      :disabled="!atur"
                      label="Tampilkan tanda tangan"
                      dense
                      hide-details
                      class="pj-person__visual"
                      @change="loadImages(true)"
                    />
                    <v-btn v-if="atur" icon small color="error" aria-label="Hapus penandatangan" @click="hapusPenandatangan(i.urutan_hirarki)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </li>
                </ol>
                <p v-else class="pj-empty">Belum ada penandatangan.</p>
              </div>
            </section>

            <!-- 4. Konfirmasi -->
            <section v-if="formData.id_jenis_dokumen_organisasi != null" class="pj-step pj-step--last">
              <h3 class="pj-step__title"><span class="pj-step__num">4</span>Periksa &amp; ajukan</h3>
              <div class="pj-confirm-box" :class="{ 'pj-confirm-box--checked': confirm }">
                <v-checkbox
                    v-model="confirm"
                    hide-details
                    dense
                    class="pj-confirm"
                    label="Saya telah memeriksa data penandatangan dan kesesuaian dokumen di atas"
                />
              </div>
            </section>
          </template>

          <div class="pj-actions">
            <v-btn text @click="back()">Batal</v-btn>
            <div class="pj-actions__main">
              <!-- why "Ajukan" is not available yet, right next to it -->
              <span v-if="uploadTypeSelected && formData.id_jenis_dokumen_organisasi != null && submitBlocker" class="pj-actions__why">
                {{ submitBlocker }}
              </span>
              <v-btn v-show="file != null" outlined color="primary" @click="preview()">
                <v-icon left small>mdi-eye-outline</v-icon>Preview &amp; atur posisi
              </v-btn>
              <v-btn v-if="uploadTypeSelected === 'per_dokumen'" depressed color="primary" :disabled="!!submitBlocker" @click="submit()">Ajukan</v-btn>
              <v-btn v-if="uploadTypeSelected === 'massal'" depressed color="primary" :disabled="!!submitBlocker" @click="submitMassal()">Ajukan</v-btn>
            </div>
          </div>
        </v-form>
        <pdf :src="sourcePdf" :page="page1" @num-pages="numPages1 = $event" style="display:none"></pdf>
      </material-card>
      <v-dialog
        v-model="dialog"
        persistent
        style="z-index:99999;"
      >
        <v-card>
          <v-card-title class="text-h5 grey lighten-2">
            Preview Dokumen
          </v-card-title>
          <v-card-text>
            <DragAndDrop
                :source="sourcePdf"
                :ttd-items="imagePdfItems"
                :dialog="dialog"
                :permission="'Tambah'"
                ref="dragdrop"
            ></DragAndDrop>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="dialog = false"
            >
              Tutup
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-overlay
        :opacity="0.5"
        :value="overlay"
      >
        <v-progress-circular indeterminate size="64">
          Loading...
        </v-progress-circular>
      </v-overlay>
    </div>
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="8000" :top="true">
      {{ snackbarText }}
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Modal Mismatch Files -->
    <v-dialog v-model="showMismatchModal" max-width="600px">
      <v-card>

        <!-- HEADER -->
        <v-card-title class="d-flex align-center red lighten-5">
          <v-icon color="red darken-2" class="mr-3" size="28">
            mdi-alert-circle
          </v-icon>
          <div>
            <div class="text-h6 font-weight-bold red--text text--darken-2">
              Ketidaksesuaian File
            </div>
            <div class="text-caption">
              Ditemukan ketidaksesuaian antara ZIP dan CSV
            </div>
          </div>
        </v-card-title>

        <!-- CONTENT -->
        <v-card-text class="pt-4">

          <!-- SUMMARY -->
          <v-alert
              type="error"
              dense
              text
              border="left"
              colored-border
              class="mb-4"
          >
            Ditemukan <strong>{{ mismatchFiles.length }}</strong> file tidak cocok
          </v-alert>

          <!-- LIST BOX -->
          <div class="mismatch-list">
            <div
                v-for="(mismatch, index) in mismatchFiles"
                :key="index"
                class="mismatch-item"
            >
              <v-icon size="18" color="red darken-1" class="mr-2">
                mdi-close-circle
              </v-icon>

              <span class="text-body-2">
      {{ mismatch }}
    </span>
            </div>
          </div>

          <!-- FOOTNOTE -->
          <div class="mt-6">
            Pastikan semua file di ZIP terdaftar di CSV dan tidak ada yang terlewat.
          </div>

        </v-card-text>

        <!-- ACTION -->
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
              color="red darken-1"
              dark
              elevation="2"
              @click="showMismatchModal = false"
          >
            Kembali dan Perbaiki
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showResultModal"
      max-width="950"
      persistent
    >
      <v-card v-if="uploadResult">
        <v-card-title
            :class="{
              'green lighten-5': uploadResult && uploadResult.status === 'ok',
              'orange lighten-5': uploadResult && uploadResult.status === 'partial',
              'red lighten-5': uploadResult && uploadResult.status === 'error'
            }"
        >
          <v-icon
              large
              class="mr-3"
              :color="
                uploadResult && uploadResult.status === 'ok'
                  ? 'green'
                  : uploadResult && uploadResult.status === 'partial'
                  ? 'orange'
                  : 'red'
              "
          >
            {{
              uploadResult && uploadResult.status === 'ok'
                  ? 'mdi-check-circle'
                  : uploadResult && uploadResult.status === 'partial'
                      ? 'mdi-alert-circle'
                      : 'mdi-close-circle'
            }}
          </v-icon>
          <div>
            <div class="text-h6 font-weight-bold">
              {{ uploadResult ? uploadResult.message : '' }}
            </div>
            <div class="text-caption">
              Hasil proses upload massal dokumen
            </div>
          </div>

        </v-card-title>

        <!-- SUMMARY -->
        <v-card-text class="pt-5">
          <v-row>
            <v-col cols="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="grey--text">Total</div>
                  <div class="text-h5">
                    {{ uploadResult.data.total }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="4">
              <v-card outlined>
                <v-card-text class="text-center green--text">
                  <div>Berhasil</div>
                  <div class="text-h5">
                    {{ uploadResult.data.success }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="4">
              <v-card outlined>
                <v-card-text class="text-center red--text">
                  <div>Gagal</div>
                  <div class="text-h5">
                    {{ uploadResult.data.failed }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-4"/>

          <v-data-table
              dense
              disable-pagination
              hide-default-footer
              :headers="[
                  { text:'Nomor Surat', value:'nomor_surat' },
                  { text:'File', value:'file_name' },
                  { text:'Status', value:'status' },
                  { text:'Pesan', value:'message' }
                ]"
              :items="uploadResult.data.detail"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                  small
                  dark
                  :color="item.status === 'success' ? 'green' : 'red'"
              >
                {{ item.status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider/>

        <v-card-actions>
          <v-spacer/>
          <v-btn
              color="primary"
              @click="closeResultDialog"
          >
            Tutup
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {apiServer, docConverter} from "../../setup-axios";
import DragAndDrop from "../jenisDokumenOrganisasi/DragAndDrop.vue";
import pdf from "vue-pdf";
import { documentPath, parafPath, qrPlaceHolder, signPath, stampPath } from '../../config/env'
import JSZip from 'jszip'

export default {
  data: () => ({
    loading: false,
    loadingHapus: false,
    pemarafError: "",
    penandatanganError: "",
    jdo: [],
    snackbar: false,
    snackbarText: "",
    snackbarColor: "",
    workflowPersetujuan: "",
    wp: true,
    duplicateValid: true,
    namaOrganisasi: "",
    org: null,
    organisasis: [],
    chosenPemarafs: [],
    chosenPenandatangans: [],
    selectedPemaraf: null,
    selectedPenandatangan: null,
    sourcePdf: null,
    sourcePdfBlob: null,
    dropdownPemarafTtd: [],
    tingkatKerahasiaan: "",
    path: window.location.pathname,
    keteranganFileTemplate: "",
    fileTemplate: "",
    displayFileTemplate: "",
    atur: false,
    uploadTypeSelected: null,
    fileMassal: {
      zip: null,
      csv: null
    },
    massalCsvData: [],
    mismatchFiles: [],
    showMismatchModal: false,
    formData: {
      id: null,
      id_jenis_dokumen_organisasi: null,
      id_status_dokumen: null,
      nomor_surat: "",
      pengguna_entry: "",
      source_apk: "internal",
      ttd_arsip_dokumen: [],
      jenis_ttd_arsip_dokumen: []
    },
    file: null,
    jenisTtds: [],
    chosenJenisTtds: [],
    chosenIdJenisTtds: [],
    confirm: false,
    deleteIndexPenandatangan: -1,
    deleteIndexPemaraf: -1,
    filePDF: null,
    overlay: false,
    dialog: false,
    organisasiFileStamp: "",
    kodeOrg: "",
    namaDokumen: "",
    peran: "",
    user: null,
    np: "",
    imagePdfItems: [],
    ttdJenisDokumen: [],
    pdfSubmit: "",
    imgParameter: "",
    numPages1: 0,
    page1: 1,
    disclaimer: null,
    disclaimerFontSize: null,
    showResultModal: false,
    uploadResult: null
  }),
  components: {DragAndDrop, pdf},
  watch: {
    'fileMassal.csv': function (newVal) {
      if (newVal) {
        this.handleCsvFileChange()
      }
    }
  },
  computed: {
    filteredOrganisasi() {
      return this.organisasis.filter(item => item.status)
    },
    // why "Ajukan" is disabled, shown next to the confirmation checkbox
    submitBlocker() {
      if (this.uploadTypeSelected === 'per_dokumen' && !this.formData.nomor_surat) return 'Isi nomor surat/dokumen untuk melanjutkan.'
      if (this.uploadTypeSelected === 'massal' && (!this.fileMassal.zip || !this.fileMassal.csv)) return 'Unggah file ZIP dan CSV untuk melanjutkan.'
      if (!this.confirm) return 'Centang konfirmasi untuk mengajukan.'
      return ''
    },
    filteredJenisDokumenOrganisasi() {
      return this.jdo.filter(item => item.Status)
    },
    jenisDokumenOrganisasiRules() {
      return [v => {
        if (!v) {
          return 'Jenis Dokumen Harus Diisi'
        }
      }
      ]
    },
    organisasiRules() {
      return [v => {
        if (!v) {
          return 'Organisasi Harus Diisi'
        }
      }
      ]
    },
    fileRules() {
      return [v => {
        if (!v) {
          return 'File Harus Diisi'
        }
      }
      ]
    },
    nomorSuratRules() {
      return [v => {
        if (!v) {
          return 'Nomor Surat Harus Diisi'
        }
      }
      ]
    },
    fileMassalZipRules() {
      return [v => {
        if (!v) {
          return 'File ZIP Harus Diisi'
        }
      }
      ]
    },
    fileMassalCsvRules() {
      return [v => {
        if (!v) {
          return 'File CSV Harus Diisi'
        }
      }
      ]
    },
  },
  created() {
    this.peran = localStorage.getItem("currentPeran")
    this.user = JSON.parse(localStorage.getItem("user"))
    console.log("uname " + this.user.username)

    this.getOrganisasi()
    this.getDisclaimerBsre()
    // this.generate_np()
  },
  methods: {
    // getOrganisasi() {
    //   apiServer
    //     .get("/arsip/organisasi/"+this.user.id)
    //     .then(response => {
    //       this.org = response.data.data;
    //       // console.log("org: " + JSON.stringify(this.org))
    //       this.formData.id_organisasi = this.org.id
    //       this.namaOrganisasi = this.org.nama
    //       this.organisasiFileStamp = this.org.file_stamp
    //       this.kodeOrg = this.org.kode_org
    //       this.setDropdownJenisDokumenOrganisasi(this.org.id)
    //     })
    //     .catch(error => {
    //       console.log("There was an error:", error.response.data.message); // Logs out the error
    //     });
    // },
    getDisclaimerBsre() {
      apiServer
        .get("/arsip/disclaimer_bsre")
        .then(response => {
          this.disclaimer = response.data.data.disclaimer;
          this.disclaimerFontSize = response.data.data.disclaimerFontSize;
          console.log("disclaimer " + JSON.stringify(response.data.data))
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
    },
    getOrganisasi() {
      apiServer
        .get("/arsip/dropdown_org")
        .then(response => {
          this.organisasis = response.data.data;
          console.log("organisasis " + JSON.stringify(this.organisasis))
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
    },
    setDropdownJenisDokumenOrganisasi(id_organisasi) {
      // console.log(id_organisasi)
      this.atur = false
      apiServer
        .get("/jenis_dokumen_organisasi/all", {
          params: {
            idOrganisasi: id_organisasi,
            idJenisDokumen: -1
          }
        })
        .then(response => {
          this.tingkatKerahasiaan = ""
          this.fileTemplate = ""
          this.chosenPemarafs = []
          this.chosenPenandatangans = []
          this.chosenIdJenisTtds = []
          this.formData.id_jenis_dokumen_organisasi = null
          this.jdo = response.data.data;
          console.log("jdo: " + JSON.stringify(this.jdo))
          this.pemarafError = ""
          this.penandatanganError = ""
          this.file = null
          this.filteredOrganisasi.forEach((item, index) => {
            if (item.id === id_organisasi) {
              this.organisasiFileStamp = item.file_stamp
              this.kodeOrg = item.kode_org
            }
          })
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
    },
    setDetailJenisDokumenOrganisasi(id_jenis_dokumen_organisasi) {
      this.atur = false
      this.file = null
      this.tingkatKerahasiaan = ""
      this.workflowPersetujuan = ""
      this.keteranganFileTemplate = ""
      this.jdo.forEach((item, index) => {
        if (item.Id === id_jenis_dokumen_organisasi) {
          this.tingkatKerahasiaan = item.TingkatKerahasiaan
          this.fileTemplate = item.FileDokumen
          this.displayFileTemplate = this.shortenFileTemplate(this.fileTemplate)
          if (this.fileTemplate === '') this.keteranganFileTemplate = 'Jenis dokumen ini tidak memiliki file template'
          this.workflowPersetujuan = item.AlurPersetujuan
          this.getDropdownJenisTtd()
          this.getDropdownPemarafTtd()
          this.getJenisDokumenOrganisasi(id_jenis_dokumen_organisasi)
          this.namaDokumen = item.NamaDokumen
        }
      })
    },
    shortenFileTemplate(file) {
      var length = 20;
      return file.length > length ? file.substring(0, length - 3) + "..." : file
    },
    aturUlang() {
      console.log(this.atur)
    },
    getDropdownJenisTtd() {
      apiServer
        .get("/arsip/jenis_ttd")
        .then(response => {
          // console.log(response.data.data)
          this.jenisTtds = response.data.data;
          this.chosenJenisTtds = []
          this.jenisTtds.forEach((item, index) => {
            this.chosenJenisTtds.push({
              id_jenis_ttd: item.Id,
              flag_visual: false,
            })
          })
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
    },
    getDropdownPemarafTtd() {
      apiServer
        .get("/arsip/dropdown_pemaraf_ttd")
        .then(response => {
          this.dropdownPemarafTtd = response.data.data;
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
    },
    generate_np() {
      apiServer
        .get("/arsip/generate_np?kode_org=PUSILKOM")
        .then(response => {
          this.np = response.data.data
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
    },
    async getJenisDokumenOrganisasi(id) {
      this.imagePdfItems = []
      this.imagePdfItemsTmp = []

      await apiServer
        .get("/jenis_dokumen_organisasi/find?id=" + id)
        .then(async response => {
          this.chosenPemarafs = []
          this.chosenPenandatangans = []
          this.chosenIdJenisTtds = []
          response.data.data.ttd_jenis_dokumen.forEach((item, index) => {
            let p = {
              id_pengguna: item.Pengguna.id,
              username: item.Pengguna.username,
              nama_lengkap: item.Pengguna.nama_lengkap,
              nik: item.Pengguna.nik,
              organisasi: item.Pengguna.Organisasi.Nama,
              file_ttd: item.Pengguna.file_ttd,
              file_paraf: item.Pengguna.file_paraf,
              jabatan: item.Pengguna.jabatan,
              urutan_hirarki: item.urutan_hirarki,
              tipe_ttd: item.tipe_ttd,
              id_jenis_ttd: item.id_jenis_ttd,
              flag_visual: item.flag_visual,
              koordinat: item.koordinat,
              halaman: item.halaman
            }
            if (item.tipe_ttd === 1) {
              this.chosenPemarafs.push(p)
            }
            if (item.tipe_ttd === 2) {
              this.chosenPenandatangans.push(p)
            }
            if (item.id_jenis_ttd) {
              // if (item.id_jenis_ttd == 1) {
              //   item.organisasi_file_stamp = this.organisasiFileStamp
              // }
              this.chosenIdJenisTtds.push(item.id_jenis_ttd)
            }
          });
          this.ttdJenisDokumen = response.data.data.ttd_jenis_dokumen

          await this.loadImages(false)
          await this.fillKoordinatTtdItems()
          this.imagePdfItemsTmp = Object.assign([], this.imagePdfItems)

        })
          .catch(error => {
            console.log("There was an error:", error.response.data.message); // Logs out the error
          });
    },
    addPemaraf() {
      console.log("adding ttd: " + JSON.stringify(this.selectedPemaraf))
      if (!this.selectedPemaraf) {
        this.pemarafError = "Pilih pemaraf untuk melakukan Tambah"
      } else if (this.workflowPersetujuan === "") {
        this.pemarafError = "Isi Jenis Dokumen per Organisasi terlebih dahulu untuk menentukan jumlah pemaraf yang dapat diisi"
      } else if (this.selectedPemaraf.file_paraf == '') {
        this.pemarafError = "Pemaraf belum memiliki file paraf"
      } else {
        this.pemarafError = ""
        let parafAppend = {
          id_pengguna: this.selectedPemaraf.id,
          username: this.selectedPemaraf.username,
          nama_lengkap: this.selectedPemaraf.nama_lengkap,
          jabatan: this.selectedPemaraf.jabatan,
          urutan_hirarki: this.chosenPemarafs.length + 1,
          nik: this.selectedPemaraf.nik,
          organisasi: this.selectedPemaraf.nama_organisasi,
          tipe_ttd: 1,
          koordinat: "",
          halaman: null,
          file_paraf: this.selectedPemaraf.file_paraf,
        }
        this.chosenPemarafs.push(parafAppend)
      }
    },
    hapusPemaraf(urutan_hirarki) {
      this.deleteIndexPemaraf = -1
      this.chosenPemarafs.forEach((item, index) => {
        if (item.urutan_hirarki === urutan_hirarki) {
          this.deleteIndexPemaraf = index
        }
      })
      if (this.deleteIndexPemaraf >= 0) {
        this.chosenPemarafs.splice(this.deleteIndexPemaraf, 1)
        this.chosenPemarafs.forEach((item, index) => {
          item.urutan_hirarki = index + 1
        })
      }
    },
    addPenandatangan() {
      console.log("adding ttd: " + JSON.stringify(this.selectedPenandatangan))
      if (!this.selectedPenandatangan) {
        this.penandatanganError = "Pilih penandatangan untuk melakukan Tambah"
      } else if (this.workflowPersetujuan === "") {
        this.penandatanganError = "Isi Jenis Dokumen per Organisasi terlebih dahulu untuk menentukan jumlah penandatangan yang dapat diisi"
      } else if (this.workflowPersetujuan === "Langsung" && this.chosenPenandatangans.length > 0) {
        this.penandatanganError = "Workflow Persetujuan Langsung hanya bisa memilih 1 penandatangan"
      } else if (this.selectedPenandatangan.file_ttd === '') {
        this.penandatanganError = "Penandatangan belum memiliki file tanda tangan"
      } else {
        this.penandatanganError = ""
        let ttdAppend = {
          id_pengguna: this.selectedPenandatangan.id,
          username: this.selectedPenandatangan.username,
          nama_lengkap: this.selectedPenandatangan.nama_lengkap,
          jabatan: this.selectedPenandatangan.jabatan,
          urutan_hirarki: this.chosenPenandatangans.length + 1,
          nik: this.selectedPenandatangan.nik,
          organisasi: this.selectedPenandatangan.nama_organisasi,
          tipe_ttd: 2,
          koordinat: "",
          halaman: null,
          file_ttd: this.selectedPenandatangan.file_ttd,
        }
        // this.selectedPenandatangan.urutan = this.chosenPenandatangans.length + 1
        this.chosenPenandatangans.push(ttdAppend)
      }
    },
    hapusPenandatangan(urutan_hirarki) {
      this.deleteIndexPenandatangan = -1
      this.chosenPenandatangans.forEach((item, index) => {
        if (item.urutan_hirarki === urutan_hirarki) {
          this.deleteIndexPenandatangan = index
        }
      })
      if (this.deleteIndexPenandatangan >= 0) {
        this.chosenPenandatangans.splice(this.deleteIndexPenandatangan, 1)
        this.chosenPenandatangans.forEach((item, index) => {
          item.urutan_hirarki = index + 1
        })
      }
    },
    async preview() {
      // this.setupFormDataBeforeSubmit()
      await this.loadImages(true)
      this.dialog = true
    },
    validateAll() {
      this.$refs.form.validate()
    },
    // set id, file path, and whether it's visual is shown on the pdf document using flag_visual
    async fillChosenJenisTtds() {
      // reset all flag visual of jenis ttd into false
      for (var ii = 0; ii < this.chosenJenisTtds.length; ii++) {
        this.chosenJenisTtds[ii].flag_visual = false
      }
      // set Id and file path of selected jenis ttd
      for (var jj = 0; jj < this.chosenIdJenisTtds.length; jj++) {
        let item = this.chosenIdJenisTtds[jj]
        let filePath = null
        let itemId = null
        if (item === 1) {
          // find stamp file for selected organisasi
          for (var kk = 0; kk < this.organisasis.length; kk++) {
            var itemOrg = this.organisasis[kk]
            if (itemOrg.id == this.formData.id_organisasi) {
              itemId = itemOrg.nama.trim().replaceAll(' ', '_') + '_organisasi'
              filePath = await this.loadBase64File(stampPath + '/' + itemOrg.file_stamp)
            }
          }
          ;
        } else if (item === 2) {
          itemId = "QRCode"
          filePath = qrPlaceHolder
        } //else {
        //     itemId = "DeskripsiQR"
        //     filePath = bsreDisclaimer
        // }
        for (var ii = 0; ii < this.chosenJenisTtds.length; ii++) {
          if (item + '' === this.chosenJenisTtds[ii].id_jenis_ttd + '') {
            this.chosenJenisTtds[ii].flag_visual = true
            this.chosenJenisTtds[ii].id = itemId
            this.chosenJenisTtds[ii].filePath = filePath
          }
        }
      }
    },
    setupFormDataBeforeSubmit() {
      this.formData.pengguna_entry = this.user.username
      this.formData.ttd_arsip_dokumen = []
      this.chosenPemarafs.forEach((item, index) => {
        if (this.workflowPersetujuan === 'Grup' || this.workflowPersetujuan == 'Langsung') item.urutan_hirarki = 1
        item.flag_urutan = item.urutan_hirarki === 1 ? true : false

        var koordinatSave = null
        var pageSave = null
        if (item.flag_visual) {
          var imagePdf = this.findElementById(item.username.replace('.', '_') + '_paraf', this.imagePdfItems)
          delete imagePdf.koordinat.base64Src
          imagePdf.koordinat.id = imagePdf.id
          koordinatSave = JSON.stringify(imagePdf.koordinat)
          pageSave = imagePdf.page
        }

        this.formData.ttd_arsip_dokumen.push({
          id_pengguna: item.id_pengguna,
          pengguna_entry: this.user.username,
          koordinat: koordinatSave,
          halaman: pageSave,
          urutan_ttd: item.urutan_hirarki,
          flag_visual: item.flag_visual,
          tipe_ttd: 1,
          flag_ttd: false,
          jabatan: item.jabatan,
          flag_urutan: item.flag_urutan
        })
      })

      this.chosenPenandatangans.forEach((item, index) => {
        if (this.workflowPersetujuan === 'Grup' || this.workflowPersetujuan == 'Langsung') item.urutan_hirarki = 1
        if (this.chosenPemarafs.length === 0) {
          item.flag_urutan = item.urutan_hirarki == 1 ? true : false
        } else {
          if (this.workflowPersetujuan === 'Berjenjang') {
            item.flag_urutan = false
          } else {
            item.flag_urutan = true
          }
        }
        var koordinatSave = null
        var pageSave = null
        if (item.flag_visual) {
          var imagePdf = this.findElementById(item.username.replace('.', '_') + '_ttd', this.imagePdfItems)
          delete imagePdf.koordinat.base64Src
          imagePdf.koordinat.id = imagePdf.id
          koordinatSave = JSON.stringify(imagePdf.koordinat)
          pageSave = imagePdf.page
        }
        this.formData.ttd_arsip_dokumen.push({
          id_pengguna: item.id_pengguna,
          pengguna_entry: this.user.username,
          koordinat: koordinatSave,
          halaman: pageSave,
          urutan_ttd: item.urutan_hirarki,
          flag_visual: item.flag_visual,
          tipe_ttd: 2,
          jabatan: item.jabatan,
          flag_ttd: false,
          flag_urutan: item.flag_urutan
        })
      })
      this.formData.jenis_ttd_arsip_dokumen = []
      //stamp, qr, deskripsi qr
      this.chosenJenisTtds.forEach((item, index) => {
        // check if chosenJenisTtds are selected
        var imagePdf = this.findElementById(item.id, this.imagePdfItems)
        if (item.flag_visual) {
          delete imagePdf.koordinat.base64Src
          imagePdf.koordinat.id = imagePdf.id
          this.formData.jenis_ttd_arsip_dokumen.push({
            id_jenis_ttd: item.id_jenis_ttd,
            flag_visual: true,
            koordinat: JSON.stringify(imagePdf.koordinat),
            halaman: imagePdf.page
          });
        }
      });
      console.log("jenis ttd " + JSON.stringify(this.chosenJenisTtds))
      console.log("form data " + JSON.stringify(this.formData))
    },
    async isValid() {
      var validPemarafTtd = this.workflowPersetujuan !== "Langsung" || this.chosenPenandatangans.length <= 1
      var notValidTtdItemWithFile = this.sourcePdf && this.imagePdfItems.filter(item => (!item.page || item.page == 0 || item.page > this.numPages1)).length > 0
      if (notValidTtdItemWithFile) {
        this.showSnackbar('error', 'Masih ada Item Tandatangan yang belum memiliki posisi pada dokumen karena dokumen yang diajukan tidak sesuai dengan posisi tandatangan yang tersimpan. Silahkan buka Preview Dokumen dan posisikan Item Tandatangan pada Dokumen')
        return false
      } else if (this.$refs.form.validate() && validPemarafTtd && (this.file != null || this.uploadTypeSelected !== 'per_dokumen')) {
        this.overlay = true
        this.pemarafError = ""
        this.penandatanganError = ""
        this.setupFormDataBeforeSubmit()
        if (this.chosenPenandatangans.length > 0) {
          if (this.formData.ttd_arsip_dokumen.length <= 1 && (this.workflowPersetujuan === 'Berjenjang' || this.workflowPersetujuan == 'Grup')) {
            this.showSnackbar("error", "Workflow Persetujuan Berjenjang atau Grup harus lebih dari 1 penandatangan");
            this.overlay = false
            return false
          } else {
            return true
          }
        } else {
          this.showSnackbar("error", "Daftar penandatangan kosong. Harap isi terlebih dahulu");
          this.overlay = false
          return false
        }
      } else if (!validPemarafTtd) {
        this.showSnackbar("error", "Workflow Persetujuan Langsung hanya bisa memilih 1 penandatangan");
        this.overlay = false
        return false
      } else if (this.uploadTypeSelected === 'per_dokumen' && this.file == null) {
        this.showSnackbar("error", "File Harus Diisi");
        this.overlay = false
        return false
      }
      this.showSnackbar("error", "Something wrong!");
      return false
    },
    async submit() {
      const isValid = this.isValid()
      if (isValid) {
        await this.setupBackendPdfInput()
        await this.setupBackendPdfStampInput()
        var formdata = new FormData()
        formdata.append("ArsipDokumen", JSON.stringify(this.formData))
        formdata.append("KodeOrganisasi", this.kodeOrg)
        formdata.append("AlurPersetujuan", this.workflowPersetujuan)
        formdata.append("NamaJenisDokumen", this.namaDokumen)
        formdata.append("PdfSource", this.pdfSubmit)
        formdata.append("ImgParameter", this.imgParameter)
        formdata.append("file", this.file)
        apiServer
          .post("/arsip/create", formdata)
          .then(response => {
            localStorage.setItem('success', "Pengajuan tandatangan digital dokumen berhasil di simpan")
            this.$router.push('/dokumen/detail/' + response.data.data.id);
            this.overlay = false
          })
          .catch(error => {
            this.overlay = false
            if (error.response.data.code === 400) {
              this.showSnackbar("error", error.response.data.message);
            } else {
              this.showSnackbar("error", error.response);
            }
          });
      }
    },
    downloadFile() {
      const timestamp = new Date().getTime()
      apiServer
          .get("/files/get-by-filepath?target_path=" + documentPath + "/" + this.fileTemplate + "&type=download&timestamp=" + timestamp, {
            responseType: "blob"
          })
          .then(response => {
            const bb = new Blob([response.data])
            const data = window.URL.createObjectURL(bb);
            // this.pdfsrc = data; //untuk tampilin di <pdf>
            const link = document.createElement('a');
            link.href = data;
            link.setAttribute('download', this.fileTemplate);
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(bb)
          })
          .catch(error => {
            console.log("There was an error:", error.response.data.message); // Logs out the error
          });
    },
    downloadTemplateCsv() {
      const link = document.createElement('a');
      link.href = '/templates/CSV_TEMPLATE_UPLOAD_MASSAL.csv';
      link.setAttribute('download', 'CSV_TEMPLATE_UPLOAD_MASSAL.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    back() {
      // console.log(this.chosenJenisTtds)
      this.$router.push('/dokumen');
    },
    showSnackbar(color, message) {
      this.snackbar = true;
      this.snackbarText = message;
      this.snackbarColor = color;
    },
    async loadSourcePdf() {
      this.sourcePdf = null
      if (this.file) {
        var blob = await new Blob([this.file], {type: 'application/pdf'})
        this.sourcePdfBlob = blob
        var urlCreator = window.URL || window.webkitURL;
        var base64Pdf = await this.convertPdfToBase64()
        const timestamp = new Date().getTime()
        console.log('url', docConverter.defaults.baseURL)
        await docConverter
          .post("/watermark-document?timestamp=" + timestamp, {
            pdfBase64: base64Pdf,
            imgParameter: "",
            username: JSON.parse(localStorage.getItem("user")).username,
            disclaimer: this.disclaimer,
            disclaimerFontSize: this.disclaimerFontSize
          }, {responseType: "blob"})
          .then(response => {
            const bb = new Blob([response.data])
            blob = bb
          })
          .catch(error => {
            console.log('watermark error')
            console.log(error)
            console.log("There was an error:", error.response.data.message); // Logs out the error
          });

        this.sourcePdf = await urlCreator.createObjectURL(blob)
      }
    },
    // drag and drop related
    findElementById(id, array) {
      for (var ii = 0; ii < array.length; ii++) {
        if ((id + '') === (array[ii].id + '')) {
          return array[ii]
        }
      }
      return null
    },
    // drag and drop related
    async loadImages(readExistingElement) {
      //alert('load')
      await this.fillChosenJenisTtds()
      var newImagePdfItems = []

      await this.chosenJenisTtds.forEach((item, index) => {
        if (item.flag_visual === true) {
          var elementExists = this.findElementById(item.id, this.imagePdfItems)
          if (elementExists && this.sourcePdf && readExistingElement) {
            newImagePdfItems.push(elementExists)
          } else {
            newImagePdfItems.push({
              path: item.filePath,
              id: item.id
            })
          }
        }
      })

      for (var ii = 0; ii < this.chosenPemarafs.length; ii++) {
        var item = this.chosenPemarafs[ii]
        var idPdfItem = item.username.replace('.', '_') + "_paraf"
        if (item.flag_visual === true) {
          var elementExists = this.findElementById(idPdfItem, this.imagePdfItems)
          if (elementExists && this.sourcePdf && readExistingElement) {
            newImagePdfItems.push(elementExists)
          } else {
            var pathBase64 = await this.loadBase64File(parafPath + '/' + item.file_paraf)
            newImagePdfItems.push({
              path: pathBase64,
              id: idPdfItem
            })
          }
        }
      }

      for (var ii = 0; ii < this.chosenPenandatangans.length; ii++) {
        var item = this.chosenPenandatangans[ii]
        var idPdfItem = item.username.replace('.', '_') + "_ttd"
        if (item.flag_visual === true) {
          var elementExists = this.findElementById(idPdfItem, this.imagePdfItems)
          console.log(this.imagePdfItems)
          if (elementExists && this.sourcePdf && readExistingElement) {
            newImagePdfItems.push(elementExists)
          } else {
            var pathBase64 = await this.loadBase64File(signPath + '/' + item.file_ttd)
            newImagePdfItems.push({
              path: pathBase64,
              id: idPdfItem
            })
          }
        }
      }

      this.imagePdfItems = newImagePdfItems
    },
    // drag and drop related
    async loadBase64File(path) {
      //alert(path)
      var base64 = ""
      const timestamp = new Date().getTime()
      await apiServer
        .get("/files/get-base64-image?target_path=" + path + "&timestamp=" + timestamp)
        .then(response => {
          base64 = response.data.data
        })
        .catch(error => {
          console.log("There was an error:", error.response.data.message); // Logs out the error
        });
      return base64
    },
    // drag and drop related
    async fillKoordinatTtdItems() {
      //loop ttd_jenis_dokumen
      await this.ttdJenisDokumen.forEach((item, index) => {
        //alert(JSON.stringify(item))
        // if the item is of pemaraf
        // use username_paraf to find element in ttdItems
        // fill koordinat in ttdItems from ttd_jenis_dokumen
        if (item.tipe_ttd === 1 && item.flag_visual) {
          var element = this.findElementById(item.Pengguna.username.replace('.', '_') + '_paraf', this.imagePdfItems)
          element.koordinat = JSON.parse(item.koordinat)
          element.page = item.halaman
        }
        // if the item is of penandatangan
        // use username_ttd to find element in ttdItems
        // fill koordinat in ttdItems from ttd_jenis_dokumen
        if (item.tipe_ttd === 2 && item.flag_visual) {
          var element = this.findElementById(item.Pengguna.username.replace('.', '_') + '_ttd', this.imagePdfItems)
          element.koordinat = JSON.parse(item.koordinat)
          element.page = item.halaman
        }
        // if the item is of jenisTtd
        // find id used in ttdItems in chosenJenisTtds
        // use id to find element in ttdItems
        // fill koordinat in ttdItems from ttd_jenis_dokumen
        if (item.id_jenis_ttd) {
          var chosenJenisTtd = null
          for (var ii = 0; ii < this.chosenJenisTtds.length; ii++) {
            if (this.chosenJenisTtds[ii].id_jenis_ttd === item.id_jenis_ttd) {
              chosenJenisTtd = this.chosenJenisTtds[ii]
            }
          }
          if (chosenJenisTtd) {
            var element = this.findElementById(chosenJenisTtd.id, this.imagePdfItems)
            element.koordinat = JSON.parse(item.koordinat)
            element.page = item.halaman
          }
        }
      });
    },
    async convertPdfToBase64() {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(this.sourcePdfBlob);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
    },
    async apiGeneratePdf() {
      var array = []
      for (var ii = 0; ii < this.imagePdfItems.length; ii++) {
        var item = Object.assign([], this.imagePdfItems[ii])
        item.koordinat.base64Src = this.imagePdfItems[ii].path
        array.push(item.koordinat)
      }
      var pdfSource = await this.convertPdfToBase64()
      const timestamp = new Date().getTime()

      return await docConverter
          .post('/generate-document-final' + '?timestamp=' + timestamp, {
            pdfBase64: pdfSource,
            imgParameter: JSON.stringify(array),
            username: JSON.parse(localStorage.getItem("user")).username
          })
          .then(response => {
            return response.data
          })
          .catch(error => {
            throw error
          });
    },
    async setupBackendPdfStampInput() {
      var array = []
      for (var ii = 0; ii < this.imagePdfItems.length; ii++) {
        var item = Object.assign([], this.imagePdfItems[ii])
        item.koordinat.base64Src = this.imagePdfItems[ii].path
        array.push(item.koordinat)
      }
      this.imgParameter = JSON.stringify(array)
    },
    async setupBackendPdfInput() {
      this.pdfSubmit = await this.convertPdfToBase64()
    },
    // Methods untuk Upload Massal
    selectUploadType(type) {
      this.uploadTypeSelected = type
      if (type === 'per_dokumen') {
        this.resetFormData()
      }
    },
    resetUploadType() {
      this.uploadTypeSelected = null
      this.fileMassal.zip = null
      this.fileMassal.csv = null
      this.massalCsvData = []
      this.resetFormData()
    },
    resetFormData() {
      this.file = null
      this.formData = {
        id: null,
        id_jenis_dokumen_organisasi: null,
        id_status_dokumen: null,
        nomor_surat: "",
        pengguna_entry: "",
        source_apk: "internal",
        ttd_arsip_dokumen: [],
        jenis_ttd_arsip_dokumen: []
      }
      this.chosenPemarafs = []
      this.chosenPenandatangans = []
      this.chosenIdJenisTtds = []
      this.atur = false
      this.confirm = false
      this.tingkatKerahasiaan = ""
      this.workflowPersetujuan = ""
      this.keteranganFileTemplate = ""
      this.fileTemplate = ""
    },
    async handleCsvFileChange() {
      if (!this.fileMassal.csv) {
        this.massalCsvData = []
        return
      }

      try {
        const file = this.fileMassal.csv
        const text = await file.text()
        const lines = text.split('\n')

        this.massalCsvData = []

        // Skip header (line 0) dan parse data dengan delimiter pipe (|)
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim() === '') continue

          const parts = lines[i].split(';').map(item => item.trim())

          if (parts.length >= 1 && parts[0]) {
            const namaFile = parts[0]
            const nomorSurat = parts[1] || ''

            this.massalCsvData.push({
              nama_file: namaFile,
              nomor_surat: nomorSurat
            })
          }
        }

        this.showSnackbar('success', 'CSV Data parsed')
      } catch (error) {
        this.showSnackbar('error', 'Gagal membaca file CSV. Pastikan format benar: Nama_File;Nomor_Surat')
      }
    },
    closeResultDialog() {
      this.showResultModal = false

      if (
          this.uploadResult &&
          (this.uploadResult.status === "ok" ||
              this.uploadResult.status === "partial")
      ) {
        localStorage.setItem(
            "success",
            "Pengajuan tandatangan digital dokumen berhasil disimpan"
        )
        this.$router.push("/dokumen")
      }
    },
    async submitMassal() {
      if (!this.fileMassal.zip || !this.fileMassal.csv) {
        this.showSnackbar("error", "File ZIP dan CSV harus diisi")
        return
      }

      if (this.massalCsvData.length === 0) {
        this.showSnackbar("error", "File CSV tidak memiliki data valid")
        return
      }

      const isValid = this.isValid()
      if (!isValid) return

      this.overlay = true

      try {
        // Validasi file ZIP dan CSV
        const zipFiles = await this.getZipFileList()
        const csvFiles = this.massalCsvData.map(item =>
            item.nama_file.toLowerCase().trim()
        )

        const mismatch = []

        for (const file of csvFiles) {
          if (!zipFiles.includes(file)) {
            mismatch.push(`"${file}" ada di CSV tapi tidak ada di ZIP`)
          }
        }

        for (const file of zipFiles) {
          if (!csvFiles.includes(file)) {
            mismatch.push(`"${file}" ada di ZIP tapi tidak ada di CSV`)
          }
        }

        if (mismatch.length > 0) {
          this.mismatchFiles = mismatch
          this.showMismatchModal = true
          return
        }

        await this.setupBackendPdfStampInput()

        const formData = new FormData()
        formData.append("ZipFile", this.fileMassal.zip)
        formData.append("CsvFile", this.fileMassal.csv)
        formData.append("ArsipDokumen", JSON.stringify(this.formData))
        formData.append("KodeOrganisasi", this.kodeOrg)
        formData.append("AlurPersetujuan", this.workflowPersetujuan)
        formData.append("NamaJenisDokumen", this.namaDokumen)
        formData.append("ImgParameter", this.imgParameter)

        const response = await apiServer.post("/arsip/create-massal", formData)

        this.uploadResult = response.data
        this.showResultModal = true
      } catch (error) {
        if (error.response) {
          const data = error.response.data

          // Semua gagal
          if (
              data.code === 400 &&
              data.data &&
              data.data.detail
          ) {
            this.uploadResult = error.response.data
            this.showResultModal = true
          } else {
            this.showSnackbar("error", data.message || "Terjadi kesalahan")
          }
        } else {
          this.showSnackbar("error", "Terjadi kesalahan saat memproses upload massal")
        }
      } finally {
        this.overlay = false
      }
    },
    async getZipFileList() {
      try {
        const zip = new JSZip()
        const zipData = await this.fileMassal.zip.arrayBuffer()
        const loadedZip = await zip.loadAsync(zipData)

        const fileList = []

        for (const [path, file] of Object.entries(loadedZip.files)) {
          if (file.dir) continue

          // Skip metadata macOS
          if (path.startsWith('__MACOSX/')) continue

          const fileName = path.split('/').pop()
          if (!fileName) continue

          // Skip AppleDouble files
          if (fileName.startsWith('._')) continue

          if (!/\.(pdf|doc|docx)$/i.test(fileName)) continue

          fileList.push(fileName.toLowerCase().trim())
        }

        return fileList
      } catch (error) {
        throw new Error('Gagal membaca file ZIP')
      }
    },
  },


}
</script>
<style scoped>
.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: #cccccc !important;
}

.mismatch-box {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 12px;
  max-height: 250px;
  overflow-y: auto;
}

.mismatch-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
}

.mismatch-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #eee;
  transition: all 0.2s ease;
}

.mismatch-item:hover {
  background: #fff5f5;
  border-color: #ffcdd2;
}
/* ---------- Form layout: type choice, numbered steps, signer lists ----------
   style.css forces p / h3 / h4 sizes with !important, so text rules here do too */
.pj-type {
  padding: 8px 0 4px;
}

.pj-type__lead {
  margin: 0 0 16px !important;
  font-size: 15px !important;
  font-weight: 600;
  color: var(--esign-text);
}

.pj-type__options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.pj-type__option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 20px;
  text-align: left;
  border: 1px solid var(--esign-border);
  border-radius: var(--esign-radius-md);
  background: var(--esign-surface);
  transition: border-color var(--esign-fast) var(--esign-ease), background-color var(--esign-fast) var(--esign-ease);
}

.pj-type__option:hover {
  border-color: var(--esign-primary);
  background: var(--esign-primary-soft);
}

.pj-type__option:focus-visible {
  outline: 2px solid var(--esign-primary);
  outline-offset: 2px;
}

.pj-type__icon.v-icon {
  margin-bottom: 4px;
  font-size: 28px;
  color: var(--esign-primary);
}

.pj-type__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--esign-text);
}

.pj-type__desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--esign-text-muted);
}

.pj-typebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  padding: 8px 8px 8px 14px;
  border-radius: var(--esign-radius-sm);
  background: var(--esign-surface-muted);
  font-size: 13px;
  font-weight: 600;
  color: var(--esign-text);
}

.pj-typebar .v-icon {
  margin-right: 6px;
  color: var(--esign-text-muted);
}

.pj-step {
  padding: 20px 0;
  border-bottom: 1px solid var(--esign-border);
}

.pj-step--last {
  border-bottom: 0;
}

.pj-step__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  line-height: 1.3 !important;
  color: var(--esign-text);
}

.pj-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--esign-primary-subtle);
  color: var(--esign-primary-strong);
  font-size: 13px;
  font-weight: 700;
}

.pj-hint {
  margin: 4px 0 0 !important;
  font-size: 12px !important;
  line-height: 18px !important;
  color: var(--esign-text-muted);
}

.pj-req {
  color: var(--esign-error);
}

.pj-muted {
  color: var(--esign-text-subtle);
  font-weight: 500;
}

.pj-info {
  margin-top: 4px;
  padding: 12px 16px;
  border-radius: var(--esign-radius-sm);
  background: var(--esign-surface-muted);
}

.pj-info__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 32px;
  margin: 0;
}

.pj-info__facts dt {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--esign-text-muted);
}

.pj-info__facts dd {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--esign-text);
}

.pj-switch {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 16px;
}

.pj-switch .v-input--switch {
  margin-top: 0;
  padding-top: 0;
}

.pj-switch__label {
  margin: 2px 0 2px !important;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 600;
  color: var(--esign-text);
}

.pj-switch__hint {
  margin: 0 !important;
  font-size: 13px !important;
  line-height: 19px !important;
  color: var(--esign-text-muted);
}

.pj-jenis-ttd {
  max-width: 560px;
  margin-bottom: 16px;
}

.pj-people + .pj-people {
  margin-top: 20px;
}

.pj-people__title {
  margin: 0 0 8px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  line-height: 1.4 !important;
  color: var(--esign-text);
}

.pj-people__add {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 720px;
  margin-bottom: 10px;
}

.pj-people__add .v-autocomplete {
  flex: 1 1 auto;
}

.pj-people__add .v-btn {
  height: 40px !important;
}

.pj-people__list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--esign-border);
  border-radius: var(--esign-radius-sm);
}

.pj-person {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
}

.pj-person + .pj-person {
  border-top: 1px solid var(--esign-border);
}

.pj-person__num {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--esign-surface-muted);
  border: 1px solid var(--esign-border);
  font-size: 12px;
  font-weight: 700;
  color: var(--esign-text-muted);
}

.pj-person__who {
  flex: 1 1 0;
  min-width: 0;
}

.pj-person__name {
  margin: 0 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 600;
  color: var(--esign-text);
}

.pj-person__meta {
  margin: 0 !important;
  font-size: 12px !important;
  line-height: 18px !important;
  color: var(--esign-text-muted);
  overflow-wrap: anywhere;
}

.pj-person__visual {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}

/* checkbox labels: the global label size is too large next to the list text */
.pj-person__visual ::v-deep .v-label {
  font-size: 13px;
  color: var(--esign-text-muted);
}

.pj-confirm ::v-deep .v-label {
  font-size: 14px;
  color: var(--esign-text);
}

.pj-empty {
  margin: 0 !important;
  padding: 12px;
  border: 1px dashed var(--esign-border-strong);
  border-radius: var(--esign-radius-sm);
  font-size: 13px !important;
  color: var(--esign-text-subtle);
  text-align: center;
}

.pj-confirm-box {
  padding: 12px 16px;
  border: 1px solid var(--esign-border);
  border-radius: var(--esign-radius-sm);
  background: var(--esign-surface-muted);
  transition: border-color var(--esign-fast) var(--esign-ease), background-color var(--esign-fast) var(--esign-ease);
}

.pj-confirm-box--checked {
  border-color: rgba(0, 125, 254, 0.35);
  background: var(--esign-primary-soft);
}

.pj-confirm {
  margin-top: 0;
  padding-top: 0;
}

.pj-confirm ::v-deep .v-input--selection-controls__input {
  margin-right: 10px;
}

.pj-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--esign-border);
}

.pj-actions__main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.pj-actions__why {
  margin-right: 4px;
  font-size: 13px;
  color: var(--esign-text-muted);
}

@media (max-width: 600px) {
  .pj-person {
    flex-wrap: wrap;
  }

  /* number, name and delete stay on one line; the checkbox goes below */
  .pj-person .v-btn {
    order: 2;
  }

  .pj-person__visual {
    order: 3;
    flex-basis: 100%;
    padding-left: 36px;
  }

  .pj-people__add {
    flex-direction: column;
    align-items: stretch;
  }

  .pj-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .pj-actions__main {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  /* column-reverse: the highest order sits on top, right above "Ajukan" */
  .pj-actions__why {
    order: 10;
    margin: 0;
    text-align: center;
  }
}
</style>